import type { WorkoutSession } from '../types/dataModel';

/** Any object with id / workoutId — session, template workout, etc. */
export type WorkoutBannerKeySource = {
  id?: string | null;
  workoutId?: string | null;
};

const unsplash = (photoId: string, width = 800) =>
  `https://images.unsplash.com/photo-${photoId}?w=${width}&q=80&fit=crop`;

/** Stock workout banner backgrounds — resolved from curator Unsplash `/photos/...` links (44 unique). */
export const WORKOUT_BANNER_STOCK_IMAGES = [
  unsplash('1534438327276-14e5300c3a48'),
  unsplash('1517836357463-d25dfeac3438'),
  unsplash('1526506118085-60ce8714f8c5'),
  unsplash('1599058917212-d750089bc07e'),
  unsplash('1571902943202-507ec2618e8f'),
  unsplash('1583454110551-21f2fa2afe61'),
  unsplash('1576678927484-cc907957088c'),
  unsplash('1550345332-09e3ac987658'),
  unsplash('1574680096145-d05b474e2155'),
  unsplash('1539794830467-1f1755804d13'),
  unsplash('1548690312-e3b507d8c110'),
  unsplash('1521804906057-1df8fdb718b7'),
  unsplash('1554284126-aa88f22d8b74'),
  unsplash('1601422407692-ec4eeec1d9b3'),
  unsplash('1620188467120-5042ed1eb5da'),
  unsplash('1722925541142-5db2668ca492'),
  unsplash('1517838277536-f5f99be501cd'),
  unsplash('1534258936925-c58bed479fcb'),
  unsplash('1591311630200-ffa9120a540f'),
  unsplash('1558611848-73f7eb4001a1'),
  unsplash('1536922246289-88c42f957773'),
  unsplash('1556817411-31ae72fa3ea0'),
  unsplash('1562771242-a02d9090c90c'),
  unsplash('1597076545399-91a3ff0e71b3'),
  unsplash('1549060279-7e168fcee0c2'),
  unsplash('1537289150563-b7f10eee353b'),
  unsplash('1517963628607-235ccdd5476c'),
  unsplash('1603665409265-bdc00027c217'),
  unsplash('1649888381057-d5d03c105f82'),
  unsplash('1674834727149-00812f907676'),
  unsplash('1593476123561-9516f2097158'),
  unsplash('1600878585887-c2b9530999a1'),
  unsplash('1723117418183-2422c62a5a75'),
  unsplash('1517963879433-6ad2b056d712'),
  unsplash('1669989179336-b2234d2878df'),
  unsplash('1683586861092-596182a95463'),
  unsplash('1516481265257-97e5f4bc50d5'),
  unsplash('1593431763017-c689a61b729a'),
  unsplash('1591258370814-01609b341790'),
  unsplash('1572432332292-6ec3392bee00'),
  unsplash('1757147517535-d0d9f4cd5b28'),
  unsplash('1632077804406-188472f1a810'),
  unsplash('1651840403913-37901945493a'),
  unsplash('1614091245298-3effbd8ed161'),
];

/**
 * Discover **Browse by Category** hero images — separate pool from workout banners.
 * Keys match `DiscoverBrowseCategory.id` on iOS.
 */
export const DISCOVER_CATEGORY_BANNER_IMAGES: Record<string, string> = {
  hiit: unsplash('1599058917212-d750089bc07e', 1200),
  strength: unsplash('1517838277536-f5f99be501cd', 1200),
  yoga: unsplash('1575052814086-f385e2e2ad1b', 1200),
  pilates: unsplash('1731325632689-7ecab02d39f3', 1200),
  cardio: unsplash('1534258936925-c58bed479fcb', 1200),
  home: unsplash('1571019613454-1cb2f99b2d8b', 1200),
};

/** Distinct URLs for gallery picker (order preserved). */
export const WORKOUT_BANNER_GALLERY_URLS: string[] = Array.from(new Set(WORKOUT_BANNER_STOCK_IMAGES));

export function isAllowlistedWorkoutBannerUrl(url: string | null | undefined): boolean {
  if (url == null || String(url).trim() === '') return false;
  const trimmed = String(url).trim();
  return (
    WORKOUT_BANNER_STOCK_IMAGES.includes(trimmed) ||
    Object.values(DISCOVER_CATEGORY_BANNER_IMAGES).includes(trimmed)
  );
}

export function getDiscoverCategoryBannerUrl(categoryId: string): string | undefined {
  return DISCOVER_CATEGORY_BANNER_IMAGES[categoryId];
}

/**
 * Prefer persisted trainer banner on the workout; else deterministic hash from workout id.
 */
export function getWorkoutDisplayBannerUrl(workout: { id: string; bannerImageUrl?: string | null }): string {
  const stored = workout.bannerImageUrl?.trim();
  if (stored && isAllowlistedWorkoutBannerUrl(stored)) return stored;
  return getWorkoutBannerImageUrl({ id: workout.id, workoutId: workout.id });
}

/**
 * Member list cards: use template banner when API embedded it; else legacy session-id hash.
 */
export function getSessionWorkoutBannerUrl(session: {
  id?: string | null;
  workoutId?: string | null;
  workout?: { id?: string; bannerImageUrl?: string | null } | null;
}): string {
  const w = session.workout;
  if (w?.id) {
    return getWorkoutDisplayBannerUrl({ id: w.id, bannerImageUrl: w.bannerImageUrl });
  }
  return getWorkoutBannerImageUrl(session);
}

/** Same modulus as `public._workout_default_banner_url` (DB migration 10/11). Keeps hash bounded; avoids bigint overflow in SQL. */
const WORKOUT_BANNER_HASH_MOD = 2147483647;

/** Stable per-session or per-workout image (hash of id / workoutId). */
export function getWorkoutBannerImageUrl(
  session: WorkoutBannerKeySource | (WorkoutSession & { workout?: any })
): string {
  let hash = 0;
  const id = String(session.id ?? session.workoutId ?? '');
  for (let i = 0; i < id.length; i++) {
    hash = (hash * 31 + id.charCodeAt(i)) % WORKOUT_BANNER_HASH_MOD;
  }
  const index = hash % WORKOUT_BANNER_STOCK_IMAGES.length;
  return WORKOUT_BANNER_STOCK_IMAGES[index];
}
